import { Badge, IconButton, Tooltip, Typography } from "@mui/material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import { FamilyRestroomOutlined } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { PageHeaderActions, type PageHeaderOverflowItem } from "@/components/common/PageHeaderActions";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ResourceName } from "@/types";
import { getResourceIcon } from "@/utils/icons";
import { useClaimantPage } from "../hooks/useClaimantPage";
import { PolicyTagSwitcher } from "./PolicyTagSwitcher";

/** A record link that reads as text and turns primary on hover: primary colour is reserved for actions. */
const quietLink = {
  color: "inherit",
  textDecoration: "none",
  "&:hover": { color: "primary.main", textDecoration: "underline" },
} as const;

interface ClaimantHeaderProps {
  onOpenTasks: () => void;
  tasksOpen: boolean;
  onLinkPolicy?: () => void;
  onManageFamily?: () => void;
}

/**
 * Claimant page header (Feature #7659): the resource icon beside the name and the
 * relationship tag as on every record page (Dillon, 2026-10-27: the header keeps
 * its shape) and the policy term with its switcher on the same title row as the
 * claim page's policy tag: the context every figure on the page belongs to. The
 * client is the second line. Ids and the birth date live in the rail card.
 * Actions: tasks with the open count inline; the rarer moves (link another
 * policy, manage the family unit) share the header's one kebab with delete. No
 * Documents icon: this page has a Documents tab.
 */
export const ClaimantHeader = ({ onOpenTasks, tasksOpen, onLinkPolicy, onManageFamily }: ClaimantHeaderProps) => {
  const { claimant, client, openTaskCount } = useClaimantPage();
  const Icon = getResourceIcon(ResourceName.CLAIMANTS);
  const TaskIcon = getResourceIcon(ResourceName.TASKS);
  const { canDelete } = useResourcePermissions(ResourceName.CLAIMANTS);
  const { canList: canListTasks } = useResourcePermissions(ResourceName.TASKS);
  const extraItems: PageHeaderOverflowItem[] = [];
  if (onLinkPolicy) {
    extraItems.push({
      key: "link-policy",
      label: "Link to another policy",
      icon: <AddLinkIcon fontSize="small" />,
      onClick: onLinkPolicy,
    });
  }
  if (onManageFamily) {
    extraItems.push({
      key: "manage-family",
      label: "Manage family unit",
      icon: <FamilyRestroomOutlined fontSize="small" />,
      onClick: onManageFamily,
    });
  }

  return (
    <PageHeader
      titleKey="claimant_name"
      titleTagKey="claimant_relationship"
      customTitleTag={claimant.claimant_relationship === "self" ? "Subscriber" : undefined}
      // Same title row as the claim page: name, tag, policy chip. The switcher sits
      // outside the h1 (titleAdornment), which is a single-line ellipsis element.
      titleAdornment={<PolicyTagSwitcher />}
      subtitleNode={
        client ? (
          <Typography component={RouterLink} to={`/clients/${client.id}/show`} variant="inherit" sx={quietLink}>
            {client.client_legal_name}
          </Typography>
        ) : undefined
      }
      headerIcon={<Icon sx={{ fontSize: 28, color: "primary.main" }} />}
      hideSectionTitle
      rightContent={
        <PageHeaderActions actions={{ delete: canDelete }} extraItems={extraItems}>
          {!tasksOpen && canListTasks ? (
            <Tooltip title={openTaskCount ? `${openTaskCount} open task${openTaskCount === 1 ? "" : "s"}` : "Tasks"}>
              <IconButton onClick={onOpenTasks} sx={{ color: "text.secondary" }} aria-label="Open tasks">
                <Badge badgeContent={openTaskCount ?? 0} color="primary" max={99}>
                  <TaskIcon />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : null}
        </PageHeaderActions>
      }
    />
  );
};
